import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { isEmpty, isNil } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import { CompanyMeasurementDate } from 'api';
import { MEASUREMENT_DATE_TITLE } from 'common/constants/allocations';
import { MEASUREMENT_DATE_ACTION_ADD, MEASUREMENT_DATE_ACTION_DELETE } from 'common/constants/measurement-date';
import { MEASUREMENT_DATE_FILTER } from 'common/constants/pageFilters';
import { useStore } from 'common/store';
import { MeasurementDate } from 'common/types/measurementDate';
import { UseStoreValues } from 'common/types/store';
import { DeleteMeasurementDateDialog } from 'components/DeleteMeasurementDate';
import { MeasurementDateDialog } from 'components/MeasurementDates';
import LayoutContext from 'context/LayoutContext';
import { useRefreshFirmMeasurementDates } from 'services/hooks/firms';
import useToggleMeasurementDateReady from 'services/hooks/useToggleMeasurementDateReady';
import { formatMeasurementDates } from 'utilities';

const useCompanyMeasurementDateFilter = () => {
  const [{ companyInfo, companyMeasurementDateList, user }] =
    useStore() as unknown as UseStoreValues;

  const { setPageFilters, setCurrentMeasurementDate, currentMeasurementDate: contextMeasurementDate } = useContext(LayoutContext);

  const { refreshFirmMeasurementDates } = useRefreshFirmMeasurementDates();

  const [mdHasBeenDeleted, setMdHasBeenDeleted] = useState(false);
  const [openMeasurementDatesDialog, setOpenMeasurementDatesDialog] = useState(false);
  const [measurementDateAction, setMeasurementDateAction] = useState(MEASUREMENT_DATE_ACTION_ADD);

  const navigate = useNavigate();
  const location = useLocation();

  // Null and the "Most Recent" sentinel (slug: '') both mean unpinned — fall back to the first company date.
  // Only pin a context date once it is confirmed in the current company's loaded list.
  const resolvedMeasurementDate = useMemo<MeasurementDate | null>(() => {
    if (!companyMeasurementDateList?.length) {
      return null;
    }

    const hasPinnedDate = !isNil(contextMeasurementDate)
      && Boolean(contextMeasurementDate.slug)
      && !isNil(contextMeasurementDate.id)
      && companyMeasurementDateList.some(md => md.id === contextMeasurementDate.id);
    return hasPinnedDate
      ? contextMeasurementDate
      : companyMeasurementDateList[0] ?? null;
  }, [contextMeasurementDate, companyMeasurementDateList]);

  // Sync resolved measurement date into LayoutContext for subtree consumers.
  // Compare by id rather than reference to avoid re-render churn when the list
  // re-fetches and returns new object references for the same measurement date.
  // Skip when the list is null (background refetch in progress) to avoid evicting
  // a valid pinned context date before the list arrives to validate it.
  useEffect(() => {
    if (isNil(companyMeasurementDateList)) return;
    if (resolvedMeasurementDate?.id !== contextMeasurementDate?.id) {
      setCurrentMeasurementDate?.(resolvedMeasurementDate);
    }
  }, [resolvedMeasurementDate, setCurrentMeasurementDate, contextMeasurementDate, companyMeasurementDateList]);

  const refreshDates = useCallback(async () => {
    if (!companyInfo?.id) {
      return;
    }
    await refreshFirmMeasurementDates({ companyId: companyInfo.id });
  }, [companyInfo?.id, refreshFirmMeasurementDates]);

  const getMeasurementDates = useCallback(
    async (newCompanyMeasurementDate?: CompanyMeasurementDate) => {
      await refreshDates();
      const newSlug = newCompanyMeasurementDate?.measurement_date?.slug;
      if (newSlug) {
        navigate(`${location.pathname}?date=${newSlug}`);
      }
    },
    [refreshDates, navigate, location.pathname]
  );

  const clearFiltersFromUrl = useCallback(() => {
    setCurrentMeasurementDate?.(null);
    navigate(location.pathname);
    setMdHasBeenDeleted(false);
  }, [navigate, location.pathname, setCurrentMeasurementDate]);

  // Clear URL date filter when a measurement date is deleted
  useEffect(() => {
    if (mdHasBeenDeleted) {
      clearFiltersFromUrl();
    }
  }, [mdHasBeenDeleted, clearFiltersFromUrl]);

  const openMeasurementDateDialog = useCallback((action = MEASUREMENT_DATE_ACTION_ADD) => {
    setOpenMeasurementDatesDialog(true);
    setMeasurementDateAction(action);
  }, []);

  const isAddingNewMD = measurementDateAction === MEASUREMENT_DATE_ACTION_ADD;

  const MDDialogue = React.memo(() => (
    <>
      {openMeasurementDatesDialog && isAddingNewMD && (
        <MeasurementDateDialog
          open={openMeasurementDatesDialog}
          getMeasurementDates={getMeasurementDates}
          onClose={() => setOpenMeasurementDatesDialog(false)}
          activeCompany={companyInfo}
        />
      )}
      {openMeasurementDatesDialog && !isAddingNewMD && (
        <DeleteMeasurementDateDialog
          open={openMeasurementDatesDialog}
          onClose={() => setOpenMeasurementDatesDialog(false)}
          getMeasurementDates={getMeasurementDates}
          setMdHasBeenDeleted={setMdHasBeenDeleted}
          selectedCmdId={resolvedMeasurementDate?.cmd_id as number | undefined}
        />
      )}
    </>
  ));

  const isStaffOrSuperuser = useMemo(() => Boolean(user?.is_staff || user?.is_superuser), [user]);

  const { handleToggleReady } = useToggleMeasurementDateReady({ refreshDates });

  const measurementDateFilter = useMemo(() => {
    if (!isEmpty(companyMeasurementDateList) && resolvedMeasurementDate) {
      const formattedOptions = formatMeasurementDates(companyMeasurementDateList ?? []);

      const optionsWithReadyToggle = isStaffOrSuperuser
        ? formattedOptions.map(option => {
          const md = companyMeasurementDateList?.find(m => m.id === option.id);
          const isReady = md?.is_ready !== false;
          const cmdId = md?.cmd_id;

          return {
            ...option,
            icon: cmdId ? (
              <Tooltip title={isReady ? 'Mark as Not Ready' : 'Mark as Ready'}>
                {isReady ? (
                  <CheckCircle fontSize="small" color="success" />
                ) : (
                  <RadioButtonUnchecked fontSize="small" color="warning" />
                )}
              </Tooltip>
            ) : undefined,
            onIconClick: cmdId ? () => handleToggleReady(cmdId, isReady) : undefined,
          };
        })
        : formattedOptions;

      return {
        id: MEASUREMENT_DATE_FILTER,
        title: MEASUREMENT_DATE_TITLE,
        options: optionsWithReadyToggle,
        selectedValue: resolvedMeasurementDate?.id as number | undefined,
        addOption: () => openMeasurementDateDialog(MEASUREMENT_DATE_ACTION_ADD),
        deleteOption: () => openMeasurementDateDialog(MEASUREMENT_DATE_ACTION_DELETE),
      };
    }
    return null;
  }, [resolvedMeasurementDate, companyMeasurementDateList, openMeasurementDateDialog, isStaffOrSuperuser, handleToggleReady]);

  useEffect(() => {
    if (measurementDateFilter) {
      setPageFilters?.([measurementDateFilter]);
    } else {
      setPageFilters?.([]);
    }
  }, [setPageFilters, measurementDateFilter]);

  return {
    selectedMeasurementDate: resolvedMeasurementDate ?? undefined,
    MDDialogue,
    openMeasurementDateDialog,
    measurementDates: companyMeasurementDateList ?? undefined,
  };
};

export default useCompanyMeasurementDateFilter;
